import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { SiteGeofenceMap } from '../components/SiteGeofenceMap';
import { 
  MapPin, 
  Plus, 
  Search,
  Building2,
  Check,
  Navigation,
  Edit,
  Trash2,
  Loader2,
  Route,
  Layers,
  Settings,
  PlayCircle,
  QrCode,
  Download,
  Target
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

export default function SitesPage() {
  const { api, isAdmin } = useAuth();
  const [sites, setSites] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);

  const [formData, setFormData] = useState({
    client_id: '',
    name: '',
    site_code: '',
    site_type: 'mobile',
    address: '',
    description: '',
    region: '',
    latitude: null,
    longitude: null,
    geofence_radius: 200
  });
  const [submitting, setSubmitting] = useState(false);
  const [siteTypeFilter, setSiteTypeFilter] = useState('all');
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [qrSite, setQrSite] = useState(null);
  const [qrToken, setQrToken] = useState(null);
  
  // Edit mode state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    site_type: 'mobile',
    address: '',
    region: '',
    description: '',
    is_active: true,
    latitude: null,
    longitude: null,
    geofence_radius: 200
  });

  // Quick setup state for locations/sectors/routes
  const [showQuickSetupDialog, setShowQuickSetupDialog] = useState(false);
  const [quickSetupSite, setQuickSetupSite] = useState(null);
  const [quickSetupLoading, setQuickSetupLoading] = useState(false);
  const [quickSetupStep, setQuickSetupStep] = useState('location'); // 'location', 'sector', 'route'
  const [quickSetupLocations, setQuickSetupLocations] = useState([]);
  const [quickSetupSectors, setQuickSetupSectors] = useState([]);
  const [quickSetupForm, setQuickSetupForm] = useState({
    locationName: '',
    locationAddress: '',
    sectorName: '',
    sectorGpsLat: '',
    sectorGpsLng: '',
    sectorGeofence: '50',
    routeName: '',
    routeDescription: ''
  });

  useEffect(() => {
    fetchData();
  }, [clientFilter]);

  const fetchData = async () => {
    try {
      let url = '/sites';
      if (clientFilter && clientFilter !== 'all') url += `?client_id=${clientFilter}`;
      
      const [sitesRes, clientsRes] = await Promise.all([
        api.get(url),
        api.get('/clients')
      ]);
      setSites(sitesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.client_id) {
      toast.error('Please select a client');
      return;
    }
    if (!formData.name.trim()) {
      toast.error('Site name is required');
      return;
    }
    if (!formData.site_code.trim()) {
      toast.error('Site code is required');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/sites', formData);
      toast.success('Site created successfully');
      fetchData();
      setShowCreateDialog(false);
      setFormData({
        client_id: '',
        name: '',
        site_code: '',
        site_type: 'mobile',
        address: '',
        description: '',
        region: '',
        latitude: null,
        longitude: null,
        geofence_radius: 200
      });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to create site';
      toast.error(errorMsg);
      console.error('Error creating site:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (siteId) => {
    if (!window.confirm('Are you sure you want to delete this site?')) return;
    try {
      await api.delete(`/sites/${siteId}`);
      toast.success('Site deleted successfully');
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to delete site';
      toast.error(errorMsg);
      console.error('Error deleting site:', error);
    }
  };

  // Open edit dialog with site data
  const openEditDialog = (site) => {
    setEditFormData({
      name: site.name || '',
      site_type: site.site_type || 'mobile',
      address: site.address || '',
      region: site.region || '',
      description: site.description || '',
      is_active: site.is_active !== false,
      latitude: site.latitude || null,
      longitude: site.longitude || null,
      geofence_radius: site.geofence_radius || 200
    });
    setShowEditDialog(true);
  };

  // Handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!editFormData.name.trim()) {
      toast.error('Site name is required');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.put(`/sites/${selectedSite.id}`, editFormData);
      toast.success('Site updated successfully');
      
      // Update the selected site with new data
      setSelectedSite(prev => ({...prev, ...editFormData}));
      
      // Refresh the sites list
      fetchData();
      setShowEditDialog(false);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to update site';
      toast.error(errorMsg);
      console.error('Error updating site:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || 'Unknown';
  };

  // Quick Setup Functions
  const openQuickSetup = async (site, e) => {
    e?.stopPropagation();
    const isStaticSite = String(site?.site_type || 'mobile').toLowerCase() === 'static';
    setQuickSetupSite(site);
    setQuickSetupStep(isStaticSite ? 'sector' : 'location');
    setQuickSetupLoading(true);
    setQuickSetupForm({
      locationName: '',
      locationAddress: site.address || '',
      sectorName: '',
      sectorGpsLat: '',
      sectorGpsLng: '',
      sectorGeofence: '50',
      routeName: `${site.name} Patrol`,
      routeDescription: `Standard patrol route for ${site.name}`
    });
    
    try {
      // Fetch existing locations for this site
      const locRes = await api.get(`/sites/${site.id}/locations`);
      let locations = locRes.data || [];

      // Static sites should not ask the user to create a Location.
      // Create/reuse one hidden/default site-level location behind the scenes.
      if (isStaticSite && locations.length === 0) {
        const createdLoc = await api.post(`/sites/${site.id}/locations`, {
          name: site.name,
          address: site.address || '',
          geofence_radius_m: site.geofence_radius || site.geofence_radius_m || 100
        });
        locations = [createdLoc.data];
      }

      setQuickSetupLocations(locations);
      
      // If locations exist, also fetch sectors
      if (locations.length > 0) {
        setQuickSetupStep('sector');
        const secRes = await api.get(`/locations/${locations[0].id}/sectors`);
        setQuickSetupSectors(secRes.data || []);
        if (!isStaticSite && secRes.data && secRes.data.length > 0) {
          setQuickSetupStep('route');
        }
      }
    } catch (error) {
      console.error('Error fetching setup data:', error);
    } finally {
      setQuickSetupLoading(false);
    }
    setShowQuickSetupDialog(true);
  };

  const handleCreateLocation = async () => {
    if (!quickSetupForm.locationName.trim()) {
      toast.error('Location name is required');
      return;
    }
    
    setQuickSetupLoading(true);
    try {
      const res = await api.post(`/sites/${quickSetupSite.id}/locations`, {
        name: quickSetupForm.locationName,
        address: quickSetupForm.locationAddress,
        geofence_radius_m: 100
      });
      
      toast.success('Location created!');
      setQuickSetupLocations([...quickSetupLocations, res.data]);
      setQuickSetupStep('sector');
      setQuickSetupForm(prev => ({ ...prev, locationName: '', locationAddress: '' }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create location');
    } finally {
      setQuickSetupLoading(false);
    }
  };

  const handleCreateSector = async () => {
    if (!quickSetupForm.sectorName.trim()) {
      toast.error('Sector name is required');
      return;
    }
    if (quickSetupLocations.length === 0) {
      toast.error('Create a location first');
      return;
    }
    
    setQuickSetupLoading(true);
    try {
      const locationId = quickSetupLocations[0].id;
      const res = await api.post(`/locations/${locationId}/sectors`, {
        name: quickSetupForm.sectorName,
        gps_lat: quickSetupForm.sectorGpsLat ? parseFloat(quickSetupForm.sectorGpsLat) : null,
        gps_lng: quickSetupForm.sectorGpsLng ? parseFloat(quickSetupForm.sectorGpsLng) : null,
        geofence_radius_m: parseInt(quickSetupForm.sectorGeofence) || 50
      });
      
      toast.success('Sector created!');
      setQuickSetupSectors([...quickSetupSectors, res.data]);
      setQuickSetupForm(prev => ({ ...prev, sectorName: '', sectorGpsLat: '', sectorGpsLng: '' }));

      const isStaticSite = String(quickSetupSite?.site_type || 'mobile').toLowerCase() === 'static';
      if (!isStaticSite) {
        setQuickSetupStep('route');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create sector');
    } finally {
      setQuickSetupLoading(false);
    }
  };

  const handleCreateRouteWithSectors = async () => {
    if (!quickSetupForm.routeName.trim()) {
      toast.error('Route name is required');
      return;
    }
    if (quickSetupSectors.length === 0) {
      toast.error('Create at least one sector first');
      return;
    }
    
    setQuickSetupLoading(true);
    try {
      // Create patrol route
      const routeRes = await api.post('/patrols/routes', {
        name: quickSetupForm.routeName,
        site_id: quickSetupSite.id,
        description: quickSetupForm.routeDescription,
        route_type: 'static',
        estimated_duration_minutes: 30,
        frequency: 'hourly'
      });
      
      // Add all sectors as stops
      const sectorIds = quickSetupSectors.map(s => s.id);
      await api.post(`/patrols/routes/${routeRes.data.id}/stops`, {
        sector_ids: sectorIds,
        mode: 'append'
      });
      
      toast.success(`Patrol route "${quickSetupForm.routeName}" created with ${sectorIds.length} checkpoint(s)!`);
      setShowQuickSetupDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create patrol route');
    } finally {
      setQuickSetupLoading(false);
    }
  };

  const handleUseDeviceGps = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setQuickSetupForm(prev => ({
            ...prev,
            sectorGpsLat: pos.coords.latitude.toFixed(6),
            sectorGpsLng: pos.coords.longitude.toFixed(6)
          }));
          toast.success('GPS location captured');
        },
        (err) => toast.error('Failed to get GPS location'),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      toast.error('Geolocation not supported');
    }
  };

  const filteredSites = sites.filter(site => {
    const matchesSearch = site.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (site.site_code || site.code || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = siteTypeFilter === 'all' || 
      (site.site_type || 'mobile') === siteTypeFilter;
    return matchesSearch && matchesType;
  });

  // Australian states and territories (P1 fix)
  const regions = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

  return (
    <div className="space-y-6" data-testid="sites-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-rajdhani font-bold text-white tracking-wide">
            SITE MANAGEMENT
          </h1>
          <p className="text-sm text-zinc-500">{sites.length} registered sites</p>
        </div>
        
        {isAdmin && (
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button 
                className="bg-blue-500 hover:bg-blue-600 text-white font-rajdhani font-semibold rounded-sm shadow-[0_0_10px_rgba(59,130,246,0.3)]"
                data-testid="create-site-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                NEW SITE
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-rajdhani text-xl">NEW SITE</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Client</Label>
                  <Select 
                    value={formData.client_id} 
                    onValueChange={(v) => setFormData({...formData, client_id: v})}
                  >
                    <SelectTrigger className="bg-[#09090b] border-[#3f3f46]">
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                      {clients.map(client => (
                        <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Site Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Site name"
                      className="bg-[#09090b] border-[#3f3f46]"
                      required
                      data-testid="site-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Code</Label>
                    <Input
                      value={formData.site_code}
                      onChange={(e) => setFormData({...formData, site_code: e.target.value.toUpperCase()})}
                      placeholder="SITE-CODE"
                      className="bg-[#09090b] border-[#3f3f46] font-mono"
                      required
                      data-testid="site-code-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Site Type</Label>
                  <Select 
                    value={formData.site_type} 
                    onValueChange={(v) => setFormData({...formData, site_type: v})}
                  >
                    <SelectTrigger className="bg-[#09090b] border-[#3f3f46]" data-testid="site-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                      <SelectItem value="mobile">
                        <div className="flex items-center gap-2">
                          <Navigation className="w-4 h-4 text-blue-400" />
                          Mobile (Patrol Runs)
                        </div>
                      </SelectItem>
                      <SelectItem value="static">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-emerald-400" />
                          Static (Sector Visits)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500">
                    {formData.site_type === 'mobile' 
                      ? 'Mobile sites use patrol runs with checkpoints.' 
                      : 'Static sites use sector visits with directives.'}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Address</Label>
                  <AddressAutocomplete
                    value={formData.address}
                    onChange={(address) => setFormData({...formData, address})}
                    onSelect={(addressData) => {
                      // Auto-fill state/region and coordinates if available
                      const updates = { 
                        address: addressData.address,
                        latitude: addressData.lat || null,
                        longitude: addressData.lng || null
                      };
                      
                      // Handle state/region - convert full names to abbreviations
                      const stateMap = {
                        "New South Wales": "NSW",
                        "Victoria": "VIC", 
                        "Queensland": "QLD",
                        "Western Australia": "WA",
                        "South Australia": "SA",
                        "Tasmania": "TAS",
                        "Northern Territory": "NT",
                        "Australian Capital Territory": "ACT",
                      };
                      
                      const stateValue = addressData.state;
                      const stateAbbrev = stateMap[stateValue] || stateValue;
                      
                      if (stateAbbrev && regions.includes(stateAbbrev)) {
                        updates.region = stateAbbrev;
                      }
                      
                      setFormData({...formData, ...updates});
                    }}
                    placeholder="e.g., 12 The Avenue, Kariong"
                    testId="site-address-input"
                  />
                </div>

                {/* Site Geofence Map */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-amber-400" />
                    Geofence Configuration
                  </Label>
                  <SiteGeofenceMap
                    latitude={formData.latitude}
                    longitude={formData.longitude}
                    geofenceRadius={formData.geofence_radius}
                    onLocationChange={(lat, lng) => setFormData({...formData, latitude: lat, longitude: lng})}
                    onRadiusChange={(radius) => setFormData({...formData, geofence_radius: radius})}
                    height="280px"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Region</Label>
                  <Select 
                    value={formData.region} 
                    onValueChange={(v) => setFormData({...formData, region: v})}
                  >
                    <SelectTrigger className="bg-[#09090b] border-[#3f3f46]">
                      <SelectValue placeholder="Select region" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                      {regions.map(region => (
                        <SelectItem key={region} value={region}>{region}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Site description..."
                    className="bg-[#09090b] border-[#3f3f46]"
                  />
                </div>
                
                <div className="flex justify-end gap-2 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowCreateDialog(false)}
                    className="border-zinc-600"
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-blue-500 hover:bg-blue-600"
                    data-testid="submit-site-btn"
                    disabled={submitting}
                  >
                    {submitting ? 'Creating...' : 'Create Site'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <Card className="bg-[#18181b] border-[#3f3f46]">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Search sites..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-[#09090b] border-[#3f3f46] text-white"
                data-testid="site-search-input"
              />
            </div>
            <Select value={siteTypeFilter} onValueChange={setSiteTypeFilter}>
              <SelectTrigger className="w-[180px] bg-[#09090b] border-[#3f3f46]" data-testid="site-type-filter">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="mobile">
                  <div className="flex items-center gap-2">
                    <Navigation className="w-4 h-4 text-blue-400" />
                    Mobile
                  </div>
                </SelectItem>
                <SelectItem value="static">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-emerald-400" />
                    Static
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="w-[200px] bg-[#09090b] border-[#3f3f46]">
                <Building2 className="w-4 h-4 mr-2 text-zinc-500" />
                <SelectValue placeholder="All Clients" />
              </SelectTrigger>
              <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                <SelectItem value="all">All Clients</SelectItem>
                {clients.map(client => (
                  <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Sites Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : filteredSites.length === 0 ? (
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="p-8 text-center text-zinc-500">
            <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No sites found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSites.map((site) => (
            <Card 
              key={site.id} 
              className="bg-[#18181b] border-[#3f3f46] hover:border-blue-500/50 transition-colors cursor-pointer"
              onClick={() => setSelectedSite(site)}
              data-testid={`site-card-${site.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${
                      (site.site_type || 'mobile') === 'static' 
                        ? 'bg-emerald-500/20' 
                        : 'bg-blue-500/20'
                    }`}>
                      {(site.site_type || 'mobile') === 'static' ? (
                        <Building2 className="w-5 h-5 text-emerald-400" />
                      ) : (
                        <Navigation className="w-5 h-5 text-blue-400" />
                      )}
                    </div>
                    <div>
                      <CardTitle className="text-base font-rajdhani text-zinc-200">
                        {site.name}
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs border-zinc-600 text-zinc-400 font-mono">
                          {site.site_code || site.code}
                        </Badge>
                        <Badge className={`text-xs ${
                          (site.site_type || 'mobile') === 'static' 
                            ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' 
                            : 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                        }`}>
                          {(site.site_type || 'mobile').toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-zinc-400 hover:text-red-400"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(site.id);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="text-xs text-zinc-500 mb-2">
                  <Building2 className="w-3 h-3 inline mr-1" />
                  {getClientName(site.client_id)}
                </div>
                
                {site.address && (
                  <div className="text-xs text-zinc-500 mb-2">
                    <Navigation className="w-3 h-3 inline mr-1" />
                    {site.address}
                  </div>
                )}
                
                <div className="flex items-center gap-2 mt-3 pt-2 border-t border-[#3f3f46]">
                  <Badge className={`text-xs ${site.is_active ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50' : 'bg-zinc-500/20 text-zinc-400 border-zinc-500/50'}`}>
                    {site.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                  {site.region && (
                    <Badge variant="outline" className="text-xs border-zinc-600 text-zinc-400">
                      {site.region}
                    </Badge>
                  )}
                  {site.geofence_radius && (
                    <Badge variant="outline" className="text-xs border-amber-500/50 text-amber-400">
                      <Target className="w-3 h-3 mr-1" />
                      {site.geofence_radius}m
                    </Badge>
                  )}
                  <span className="text-xs text-zinc-600 flex items-center gap-1 ml-auto">
                    <Check className="w-3 h-3" />
                    {site.checkpoints?.length || 0} checkpoints
                  </span>
                </div>
                
                {/* Quick Setup Button for MOBILE sites */}
                {(site.site_type || 'mobile') === 'mobile' && isAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-3 border-blue-500/50 text-blue-400 hover:bg-blue-500/10 text-xs"
                    onClick={(e) => openQuickSetup(site, e)}
                    data-testid={`setup-patrol-btn-${site.id}`}
                  >
                    <Settings className="w-3 h-3 mr-2" />
                    Setup Patrol Route & Checkpoints
                  </Button>
                )}
                
                {/* Site Actions */}
                {isAdmin && (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full border-emerald-500 text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 text-xs font-semibold"
                      onClick={(e) => openQuickSetup(site, e)}
                      data-testid={`manage-sectors-btn-${site.id}`}
                    >
                      <Settings className="w-3 h-3 mr-2" />
                      Manage Sectors
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full border-emerald-500 text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 text-xs font-semibold"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const response = await api.get(`/qr/site/${site.id}/qr-data`);
                          setQrToken(response.data.token);
                          setQrSite(site);
                          setShowQRDialog(true);
                        } catch (error) {
                          toast.error('Failed to generate QR code');
                        }
                      }}
                      data-testid={`qr-code-btn-${site.id}`}
                    >
                      <QrCode className="w-3 h-3 mr-2" />
                      Site QR Code
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Site Detail Dialog */}
      <Dialog open={!!selectedSite && !showEditDialog} onOpenChange={() => setSelectedSite(null)}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedSite && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-sm bg-emerald-500/20 flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div>
                      <DialogTitle className="font-rajdhani text-xl">{selectedSite.name}</DialogTitle>
                      <span className="text-sm text-zinc-400 font-mono">{selectedSite.site_code || selectedSite.code}</span>
                    </div>
                  </div>
                  {isAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                      onClick={() => openEditDialog(selectedSite)}
                      data-testid="edit-site-btn"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                  )}
                </div>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-zinc-500">Client:</span>
                    <span className="ml-2 text-zinc-200">{getClientName(selectedSite.client_id)}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Region:</span>
                    <span className="ml-2 text-zinc-200">{selectedSite.region || '-'}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Type:</span>
                    <Badge className={`ml-2 text-xs ${
                      (selectedSite.site_type || 'mobile') === 'static' 
                        ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' 
                        : 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                    }`}>
                      {(selectedSite.site_type || 'mobile').toUpperCase()}
                    </Badge>
                  </div>
                  <div>
                    <span className="text-zinc-500">Status:</span>
                    <Badge className={`ml-2 text-xs ${selectedSite.is_active !== false ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                      {selectedSite.is_active !== false ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div>
                    <span className="text-zinc-500">Geofence:</span>
                    <Badge className="ml-2 text-xs bg-amber-500/20 text-amber-400 border-amber-500/30">
                      <Target className="w-3 h-3 mr-1" />
                      {selectedSite.geofence_radius || 200}m radius
                    </Badge>
                  </div>
                  {selectedSite.latitude && selectedSite.longitude && (
                    <div>
                      <span className="text-zinc-500">GPS:</span>
                      <span className="ml-2 text-zinc-300 font-mono text-xs">
                        {selectedSite.latitude.toFixed(4)}, {selectedSite.longitude.toFixed(4)}
                      </span>
                    </div>
                  )}
                </div>
                
                {selectedSite.address && (
                  <div>
                    <span className="text-zinc-500 text-sm">Address:</span>
                    <p className="text-zinc-200 text-sm mt-1">{selectedSite.address}</p>
                  </div>
                )}
                
                {/* Site Map Preview (read-only) */}
                {selectedSite.latitude && selectedSite.longitude && (
                  <SiteGeofenceMap
                    latitude={selectedSite.latitude}
                    longitude={selectedSite.longitude}
                    geofenceRadius={selectedSite.geofence_radius || 200}
                    readOnly={true}
                    showControls={false}
                    height="200px"
                  />
                )}
                
                {selectedSite.description && (
                  <div>
                    <span className="text-zinc-500 text-sm">Description:</span>
                    <p className="text-zinc-200 text-sm mt-1">{selectedSite.description}</p>
                  </div>
                )}
                
                {/* Checkpoints */}
                <div>
                  <h4 className="text-sm font-rajdhani font-semibold text-zinc-400 uppercase tracking-wider mb-3">
                    CHECKPOINTS ({selectedSite.checkpoints?.length || 0})
                  </h4>
                  {selectedSite.checkpoints && selectedSite.checkpoints.length > 0 ? (
                    <div className="space-y-2">
                      {selectedSite.checkpoints.map((cp, idx) => (
                        <div 
                          key={cp.id} 
                          className="flex items-center justify-between p-3 bg-[#09090b] rounded border border-[#3f3f46]"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-mono text-zinc-500 w-6">{idx + 1}.</span>
                            <div>
                              <span className="text-sm text-zinc-200">{cp.name}</span>
                              <Badge variant="outline" className="ml-2 text-xs border-zinc-600 text-zinc-500">
                                {cp.checkpoint_type}
                              </Badge>
                            </div>
                          </div>
                          {cp.photo_required && (
                            <Badge className="text-xs bg-yellow-500/20 text-yellow-400 border-yellow-500/50">
                              Photo Required
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-zinc-500 text-sm">No checkpoints defined</p>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Site Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-xl">EDIT SITE</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Site Name <span className="text-red-400">*</span></Label>
              <Input
                value={editFormData.name}
                onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                placeholder="Site name"
                className="bg-[#09090b] border-[#3f3f46]"
                required
                data-testid="edit-site-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Site Type</Label>
              <Select 
                value={editFormData.site_type} 
                onValueChange={(v) => setEditFormData({...editFormData, site_type: v})}
              >
                <SelectTrigger className="bg-[#09090b] border-[#3f3f46]" data-testid="edit-site-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                  <SelectItem value="mobile">
                    <div className="flex items-center gap-2">
                      <Navigation className="w-4 h-4 text-blue-400" />
                      Mobile (Patrol Runs)
                    </div>
                  </SelectItem>
                  <SelectItem value="static">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-emerald-400" />
                      Static (Sector Visits)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Address</Label>
              <AddressAutocomplete
                value={editFormData.address}
                onChange={(address) => setEditFormData({...editFormData, address})}
                onSelect={(addressData) => {
                  const updates = { 
                    address: addressData.address,
                    latitude: addressData.lat || editFormData.latitude,
                    longitude: addressData.lng || editFormData.longitude
                  };
                  
                  // Handle state/region - convert full names to abbreviations
                  const stateMap = {
                    "New South Wales": "NSW",
                    "Victoria": "VIC", 
                    "Queensland": "QLD",
                    "Western Australia": "WA",
                    "South Australia": "SA",
                    "Tasmania": "TAS",
                    "Northern Territory": "NT",
                    "Australian Capital Territory": "ACT",
                  };
                  
                  const stateValue = addressData.state;
                  const stateAbbrev = stateMap[stateValue] || stateValue;
                  
                  if (stateAbbrev && regions.includes(stateAbbrev)) {
                    updates.region = stateAbbrev;
                  }
                  
                  setEditFormData({...editFormData, ...updates});
                }}
                placeholder="e.g., 109 Mount Vincent Rd"
                testId="edit-site-address-input"
              />
            </div>

            {/* Site Geofence Map */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Target className="w-4 h-4 text-amber-400" />
                Geofence Configuration
              </Label>
              <SiteGeofenceMap
                latitude={editFormData.latitude}
                longitude={editFormData.longitude}
                geofenceRadius={editFormData.geofence_radius}
                onLocationChange={(lat, lng) => setEditFormData({...editFormData, latitude: lat, longitude: lng})}
                onRadiusChange={(radius) => setEditFormData({...editFormData, geofence_radius: radius})}
                height="280px"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Region</Label>
              <Select 
                value={editFormData.region} 
                onValueChange={(v) => setEditFormData({...editFormData, region: v})}
              >
                <SelectTrigger className="bg-[#09090b] border-[#3f3f46]">
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                  {regions.map(region => (
                    <SelectItem key={region} value={region}>{region}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={editFormData.description}
                onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                placeholder="Site description..."
                className="bg-[#09090b] border-[#3f3f46]"
              />
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select 
                value={editFormData.is_active ? 'active' : 'inactive'} 
                onValueChange={(v) => setEditFormData({...editFormData, is_active: v === 'active'})}
              >
                <SelectTrigger className="bg-[#09090b] border-[#3f3f46]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <DialogFooter className="pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowEditDialog(false)}
                className="border-zinc-600"
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-blue-500 hover:bg-blue-600"
                data-testid="save-site-btn"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quick Setup Patrol Dialog */}
      <Dialog open={showQuickSetupDialog} onOpenChange={setShowQuickSetupDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-xl flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-400" />
                {(quickSetupSite?.site_type || 'mobile') === 'static'
                  ? `Manage Sectors: ${quickSetupSite?.name}`
                  : `Setup Patrol: ${quickSetupSite?.name}`}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
                {(quickSetupSite?.site_type || 'mobile') === 'static'
                  ? 'Create and manage operational sectors for this site'
                  : 'Create locations, sectors (checkpoints), and patrol route for this site'}
            </DialogDescription>
          </DialogHeader>

          {quickSetupLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          )}

          {!quickSetupLoading && (
            <div className="space-y-6">
              {/* Progress Indicator */}
              <div className="flex items-center gap-2 text-xs">
                <div className={`flex items-center gap-1 px-2 py-1 rounded ${quickSetupStep === 'location' ? 'bg-blue-500/20 text-blue-400' : quickSetupLocations.length > 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-700 text-zinc-400'}`}>
                  <Layers className="w-3 h-3" />
                  1. Location {quickSetupLocations.length > 0 && `(${quickSetupLocations.length})`}
                </div>
                <span className="text-zinc-600">→</span>
                <div className={`flex items-center gap-1 px-2 py-1 rounded ${quickSetupStep === 'sector' ? 'bg-blue-500/20 text-blue-400' : quickSetupSectors.length > 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-700 text-zinc-400'}`}>
                  <MapPin className="w-3 h-3" />
                  2. Sectors {quickSetupSectors.length > 0 && `(${quickSetupSectors.length})`}
                </div>
                <span className="text-zinc-600">→</span>
                <div className={`flex items-center gap-1 px-2 py-1 rounded ${quickSetupStep === 'route' ? 'bg-blue-500/20 text-blue-400' : 'bg-zinc-700 text-zinc-400'}`}>
                  <Route className="w-3 h-3" />
                  3. Route
                </div>
              </div>

              {/* Step 1: Location */}
              {quickSetupStep === 'location' && (
                <div className="space-y-4 p-4 bg-[#09090b] rounded border border-[#3f3f46]">
                  <h3 className="text-sm font-semibold text-zinc-300 flex items-center gap-2">
                    <Layers className="w-4 h-4 text-blue-400" />
                    Step 1: Create a Location
                  </h3>
                  <p className="text-xs text-zinc-500">Locations are areas within your site (e.g., "Main Building", "Car Park")</p>
                  
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-zinc-400">Location Name *</Label>
                      <Input
                        value={quickSetupForm.locationName}
                        onChange={(e) => setQuickSetupForm(prev => ({ ...prev, locationName: e.target.value }))}
                        placeholder="e.g., Main Facility, Admin Building"
                        className="bg-[#18181b] border-[#3f3f46] text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-zinc-400">Address (optional)</Label>
                      <Input
                        value={quickSetupForm.locationAddress}
                        onChange={(e) => setQuickSetupForm(prev => ({ ...prev, locationAddress: e.target.value }))}
                        placeholder="Street address"
                        className="bg-[#18181b] border-[#3f3f46] text-sm"
                      />
                    </div>
                    <Button onClick={handleCreateLocation} className="w-full bg-blue-500 hover:bg-blue-600">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Location
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 2: Sectors */}
              {quickSetupStep === 'sector' && (
                <div className="space-y-4 p-4 bg-[#09090b] rounded border border-[#3f3f46]">
                  <h3 className="text-sm font-semibold text-zinc-300 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-400" />
                      {(quickSetupSite?.site_type || 'mobile') === 'static'
                        ? 'Manage Sectors'
                        : 'Step 2: Add Sectors (Checkpoints)'}
                  </h3>
                    <p className="text-xs text-zinc-500">
                      {(quickSetupSite?.site_type || 'mobile') === 'static'
                        ? 'Create operational sectors such as Front Gate, CRC Area, Area 1, Area 4 and Tip Face'
                        : 'Sectors are specific points guards must check (e.g., Front Gate, Loading Dock)'}
                    </p>
                  
                  {/* Existing Sectors */}
                  {quickSetupSectors.length > 0 && (
                    <div className="space-y-1">
                      <Label className="text-xs text-zinc-400">Created Sectors:</Label>
                      <div className="flex flex-wrap gap-1">
                        {quickSetupSectors.map(s => (
                          <Badge key={s.id} className="bg-emerald-500/20 text-emerald-400 text-xs">
                            {s.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-zinc-400">Sector Name *</Label>
                      <Input
                        value={quickSetupForm.sectorName}
                        onChange={(e) => setQuickSetupForm(prev => ({ ...prev, sectorName: e.target.value }))}
                        placeholder="e.g., Front Gate, Reception, Loading Bay"
                        className="bg-[#18181b] border-[#3f3f46] text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-zinc-400">GPS Coordinates (optional)</Label>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm" 
                          onClick={handleUseDeviceGps}
                          className="text-xs text-blue-400 h-6"
                        >
                          <Navigation className="w-3 h-3 mr-1" />
                          Use GPS
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          value={quickSetupForm.sectorGpsLat}
                          onChange={(e) => setQuickSetupForm(prev => ({ ...prev, sectorGpsLat: e.target.value }))}
                          placeholder="Latitude"
                          className="bg-[#18181b] border-[#3f3f46] text-sm"
                        />
                        <Input
                          value={quickSetupForm.sectorGpsLng}
                          onChange={(e) => setQuickSetupForm(prev => ({ ...prev, sectorGpsLng: e.target.value }))}
                          placeholder="Longitude"
                          className="bg-[#18181b] border-[#3f3f46] text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleCreateSector} className="flex-1 bg-blue-500 hover:bg-blue-600">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Sector
                      </Button>
                      {quickSetupSectors.length > 0 &&
                        String(quickSetupSite?.site_type || 'mobile').toLowerCase() !== 'static' && (
                        <Button 
                          variant="outline" 
                          onClick={() => setQuickSetupStep('route')}
                          className="border-emerald-500/50 text-emerald-400"
                        >
                          Next →
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Route */}
              {quickSetupStep === 'route' &&
                String(quickSetupSite?.site_type || 'mobile').toLowerCase() !== 'static' && (
                <div className="space-y-4 p-4 bg-[#09090b] rounded border border-[#3f3f46]">
                  <h3 className="text-sm font-semibold text-zinc-300 flex items-center gap-2">
                    <Route className="w-4 h-4 text-blue-400" />
                    Step 3: Create Patrol Route
                  </h3>
                  <p className="text-xs text-zinc-500">Create a patrol route that includes all your sectors as checkpoints</p>
                  
                  {/* Summary */}
                  <div className="p-3 bg-[#18181b] rounded border border-[#3f3f46]">
                    <div className="text-xs text-zinc-400 mb-2">Route will include {quickSetupSectors.length} checkpoint(s):</div>
                    <div className="flex flex-wrap gap-1">
                      {quickSetupSectors.map((s, idx) => (
                        <Badge key={s.id} className="bg-blue-500/20 text-blue-400 text-xs">
                          {idx + 1}. {s.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-zinc-400">Route Name *</Label>
                      <Input
                        value={quickSetupForm.routeName}
                        onChange={(e) => setQuickSetupForm(prev => ({ ...prev, routeName: e.target.value }))}
                        placeholder="e.g., Night Patrol Route"
                        className="bg-[#18181b] border-[#3f3f46] text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-zinc-400">Description (optional)</Label>
                      <Textarea
                        value={quickSetupForm.routeDescription}
                        onChange={(e) => setQuickSetupForm(prev => ({ ...prev, routeDescription: e.target.value }))}
                        placeholder="Brief description of the patrol route..."
                        className="bg-[#18181b] border-[#3f3f46] text-sm"
                        rows={2}
                      />
                    </div>
                    <Button onClick={handleCreateRouteWithSectors} className="w-full bg-emerald-500 hover:bg-emerald-600">
                      <PlayCircle className="w-4 h-4 mr-2" />
                      Create Patrol Route
                    </Button>
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="flex gap-2 pt-2 border-t border-[#3f3f46]">
                {quickSetupStep !== 'location' && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      const isStaticSite = String(quickSetupSite?.site_type || 'mobile').toLowerCase() === 'static';
                      setQuickSetupStep(quickSetupStep === 'route' || isStaticSite ? 'sector' : 'location');
                    }}
                    className="text-zinc-400"
                  >
                    ← Back
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowQuickSetupDialog(false)}
                  className="ml-auto border-zinc-600"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Site QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-blue-400" />
              Site QR Code
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Print or download this QR code for {qrSite?.name}
            </DialogDescription>
          </DialogHeader>
          
          {qrToken && (
            <div className="space-y-4">
              {/* QR Code Display */}
              <div className="flex justify-center p-6 bg-white rounded-lg">
                <QRCodeSVG 
                  value={qrToken}
                  size={200}
                  level="M"
                  includeMargin={true}
                />
              </div>
              
              {/* Site Info */}
              <div className="text-center space-y-1">
                <p className="text-white font-medium">{qrSite?.name}</p>
                <p className="text-zinc-500 text-sm">{qrSite?.address}</p>
              </div>
              
              {/* Instructions */}
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-md">
                <p className="text-blue-400 text-xs">
                  Officers can scan this QR code to verify their location when checking in or out of this site.
                </p>
              </div>
              
              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 border-zinc-600"
                  onClick={() => {
                    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/qr/site/${qrSite?.id}/qr`, '_blank');
                  }}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PNG
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-zinc-600"
                  onClick={() => {
                    window.print();
                  }}
                >
                  Print
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
